import React, { useState } from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
  Button,
  Input,
  Textarea,
  Dropdown,
  Option,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Text,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Label,
  Tooltip,
} from '@fluentui/react-components';
import {
  Edit24Regular,
  Delete24Regular,
  Wrench24Regular,
  QuestionCircle24Regular,
  Calendar24Regular,
} from '@fluentui/react-icons';
import maintenanceHistoryData from '../mocks/maintenance-history.json';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#edf1f5',
    ...shorthands.padding('16px'),
  },
  filterBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '16px',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.padding('12px', '16px'),
    ...shorthands.borderRadius('8px'),
    boxShadow: tokens.shadow8,
  },
  filterGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  filterLabel: {
    fontSize: '14px',
    fontWeight: '600',
    minWidth: 'fit-content',
  },
  deviceToggleGroup: {
    display: 'flex',
    gap: '0px',
  },
  unitsToggleGroup: {
    display: 'flex',
    gap: '0px',
  },
  tableContainer: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px'),
    boxShadow: tokens.shadow8,
    ...shorthands.overflow('hidden'),
  },
  tableHeader: {
    backgroundColor: '#3d3d3d',
    color: '#ffffff',
  },
  tableHeaderRow: {
    backgroundColor: '#3d3d3d',
  },
  tableHeaderCell: {
    color: '#ffffff',
    fontWeight: '600',
    fontSize: '14px',
  },
  filterSearch: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  serviceIcon: {
    marginRight: '8px',
  },
  headerWithIcon: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  notesTextarea: {
    width: '100%',
    marginTop: '8px',
  },
});

interface MaintenanceHistoryRecord {
  id: string;
  trackerItem: string;
  serviceType: string;
  odometer: string;
  engineHours: string;
  usageHours: string;
  dateCompleted: string;
  cost: string;
  notes: string;
}

//Fetch mock data in dev. Switch to real data in prod
const historyData: MaintenanceHistoryRecord[] = maintenanceHistoryData;

export default function MaintenanceHistory() {
  const styles = useStyles();
  const [dateRange, setDateRange] = useState('Select date range');
  const [groupFilter, setGroupFilter] = useState('Select group');
  const [serviceTypeFilter, setServiceTypeFilter] = useState('Select service type');
  const [deviceFilter, setDeviceFilter] = useState('Select device');
  const [unitsFilter, setUnitsFilter] = useState('Miles');
  const [searchFilter, setSearchFilter] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<MaintenanceHistoryRecord | null>(null);

  // Export function
  const exportToCSV = () => {
    const headers = ['Tracker/Item', 'Service Type', 'Odometer', 'Engine Hours', 'Usage Hours', 'Date Completed', 'Cost', 'Notes'];
    const rows = historyData.map(record => [
      record.trackerItem,
      record.serviceType,
      record.odometer,
      record.engineHours,
      record.usageHours,
      record.dateCompleted,
      record.cost,
      record.notes,
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `maintenance_history_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Print function
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Maintenance History - ${new Date().toLocaleDateString()}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
            }
            h1 {
              font-size: 24px;
              margin-bottom: 20px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            th {
              background-color: #3d3d3d;
              color: white;
              font-weight: 600;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            @media print {
              body {
                padding: 0;
              }
            }
          </style>
        </head>
        <body>
          <h1>Maintenance History</h1>
          <p>Generated on: ${new Date().toLocaleString()}</p>
          <table>
            <thead>
              <tr>
                <th>Tracker/Item</th>
                <th>Service Type</th>
                <th>Odometer</th>
                <th>Engine Hours</th>
                <th>Usage Hours</th>
                <th>Date Completed</th>
                <th>Cost</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              ${historyData.map(record => `
                <tr>
                  <td>${record.trackerItem}</td>
                  <td>${record.serviceType}</td>
                  <td>${record.odometer}</td>
                  <td>${record.engineHours}</td>
                  <td>${record.usageHours}</td>
                  <td>${record.dateCompleted}</td>
                  <td>${record.cost}</td>
                  <td>${record.notes}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  // Listen for export event
  React.useEffect(() => {
    const handleExport = (event: CustomEvent) => {
      if (event.detail === 'history') {
        exportToCSV();
      }
    };

    window.addEventListener('maintenanceExport', handleExport as EventListener);
    return () => {
      window.removeEventListener('maintenanceExport', handleExport as EventListener);
    };
  }, []);

  // Listen for print event
  React.useEffect(() => {
    const handlePrintEvent = (event: CustomEvent) => {
      if (event.detail === 'history') {
        handlePrint();
      }
    };

    window.addEventListener('maintenancePrint', handlePrintEvent as EventListener);
    return () => {
      window.removeEventListener('maintenancePrint', handlePrintEvent as EventListener);
    };
  }, []);

  // Edit form state
  const [editTracker, setEditTracker] = useState('');
  const [editCompletionDate, setEditCompletionDate] = useState('');
  const [editOdoAtCompletion, setEditOdoAtCompletion] = useState('');
  const [editServiceType, setEditServiceType] = useState('');
  const [editCost, setEditCost] = useState('');
  const [editNotes, setEditNotes] = useState('');

  const handleEdit = (record: MaintenanceHistoryRecord) => {
    setSelectedRecord(record);
    setEditTracker(record.trackerItem);
    setEditCompletionDate('Tue, 10/14/2025');
    setEditOdoAtCompletion('899657');
    setEditServiceType(record.serviceType);
    setEditCost('');
    setEditNotes('');
    setEditDialogOpen(true);
  };

  return (
    <div className={styles.container}>
      {/* Filter Bar */}
      <div className={styles.filterBar}>
        <div className={styles.filterGroup}>
          <Dropdown
            value={dateRange}
            onOptionSelect={(_, data) => setDateRange(data.optionText || dateRange)}
            style={{ minWidth: '200px' }}
          >
            <Option>Last 7 days</Option>
            <Option>Last 30 days</Option>
            <Option>Last 90 days</Option>
          </Dropdown>
        </div>

        <div className={styles.filterGroup}>
          <Dropdown
            value={groupFilter}
            onOptionSelect={(_, data) => setGroupFilter(data.optionText || 'All')}
            style={{ minWidth: '150px' }}
          >
            <Option>All</Option>
            <Option>Fleet A</Option>
            <Option>Fleet B</Option>
          </Dropdown>
        </div>

        <div className={styles.filterGroup}>
          <Dropdown
            value={serviceTypeFilter}
            onOptionSelect={(_, data) => setServiceTypeFilter(data.optionText || 'All')}
            style={{ minWidth: '150px' }}
          >
            <Option>All</Option>
            <Option>Oil Change</Option>
            <Option>Air Filter</Option>
          </Dropdown>
        </div>

        <div className={styles.filterGroup}>
          <Dropdown
            value={deviceFilter}
            onOptionSelect={(_, data) => setDeviceFilter(data.optionText || 'Select device')}
            style={{ minWidth: '150px' }}
          >
            <Option>All</Option>
            <Option>Tracker</Option>
            <Option>Item</Option>
          </Dropdown>
        </div>

        <div className={styles.filterGroup}>
          <Dropdown
            value={unitsFilter}
            onOptionSelect={(_, data) => setUnitsFilter(data.optionText || 'Select units')}
            style={{ minWidth: '150px' }}
          >
            <Option>Miles</Option>
            <Option>KMs</Option>
          </Dropdown>
        </div>
        <div className={styles.filterSearch}>
          <Input
            placeholder="Tracker/Item"
            value={searchFilter}
            onChange={(e, data) => setSearchFilter(data.value)}
            style={{ minWidth: '200px' }}
          />
          <Button appearance="primary">MANAGE ITEMS</Button>
        </div>
      </div>

      {/* Table */}
      <div className={styles.tableContainer}>
        <Table>
          <TableHeader className={styles.tableHeader}>
            <TableRow className={styles.tableHeaderRow}>
              <TableHeaderCell className={styles.tableHeaderCell}>
                Tracker/Item
              </TableHeaderCell>
              <TableHeaderCell className={styles.tableHeaderCell}>
                Service Type
              </TableHeaderCell>
              <TableHeaderCell className={styles.tableHeaderCell}>
                Odometer
              </TableHeaderCell>
              <TableHeaderCell className={styles.tableHeaderCell}>
                <div className={styles.headerWithIcon}>
                  Engine Hours
                  <Tooltip content="Engine hours is strictly the amount of time there was power on the white wire (Ignition On to Ignition Off)" relationship="label">
                    <QuestionCircle24Regular style={{ fontSize: '16px', cursor: 'help' }} />
                  </Tooltip>
                  ↑
                </div>
              </TableHeaderCell>
              <TableHeaderCell className={styles.tableHeaderCell}>
                <div className={styles.headerWithIcon}>
                  Usage Hours
                  <Tooltip content="Usage hours is both the time the engine was running (power on the white wire) and any motion periods where the engine was not running." relationship="label">
                    <QuestionCircle24Regular style={{ fontSize: '16px', cursor: 'help' }} />
                  </Tooltip>
                </div>
              </TableHeaderCell>
              <TableHeaderCell className={styles.tableHeaderCell}>
                Date Completed
              </TableHeaderCell>
              <TableHeaderCell className={styles.tableHeaderCell}>Cost</TableHeaderCell>
              <TableHeaderCell className={styles.tableHeaderCell}>Notes</TableHeaderCell>
              <TableHeaderCell className={styles.tableHeaderCell}>Edit</TableHeaderCell>
              <TableHeaderCell className={styles.tableHeaderCell}>Delete</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {historyData.map((record) => (
              <TableRow key={record.id}>
                <TableCell>{record.trackerItem}</TableCell>
                <TableCell>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Wrench24Regular className={styles.serviceIcon} />
                    {record.serviceType}
                  </div>
                </TableCell>
                <TableCell>{record.odometer}</TableCell>
                <TableCell>{record.engineHours}</TableCell>
                <TableCell>{record.usageHours}</TableCell>
                <TableCell>{record.dateCompleted}</TableCell>
                <TableCell>{record.cost}</TableCell>
                <TableCell>{record.notes}</TableCell>
                <TableCell>
                  <Button appearance="subtle" icon={<Edit24Regular />} onClick={() => handleEdit(record)} />
                </TableCell>
                <TableCell>
                  <Button appearance="subtle" icon={<Delete24Regular />} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Edit Completed Service Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(_, data) => setEditDialogOpen(data.open)}>
        <DialogSurface style={{ maxWidth: '400px' }}>
          <DialogBody>
            <DialogTitle>Edit Completed Service</DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <Label htmlFor="tracker">Tracker</Label>
                  <Text style={{ display: 'block', marginTop: '8px' }}>{editTracker}</Text>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <Label htmlFor="completionDate">Service Completion Date</Label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px' }}>
                      <Input
                        id="completionDate"
                        value={editCompletionDate}
                        onChange={(e, data) => setEditCompletionDate(data.value)}
                        style={{ width: '100%' }}
                      />
                      <Button appearance="subtle" icon={<Calendar24Regular />} />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="odoAtCompletion">Odo (km) At Time of Completion</Label>
                    <Input
                      id="odoAtCompletion"
                      value={editOdoAtCompletion}
                      onChange={(e, data) => setEditOdoAtCompletion(data.value)}
                      style={{ width: '100%', marginTop: '8px' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <Label htmlFor="serviceType">Service Type</Label>
                    <Dropdown
                      id="serviceType"
                      value={editServiceType}
                      onOptionSelect={(_, data) => setEditServiceType(data.optionText || '')}
                      style={{ width: '100%', marginTop: '8px' }}
                    >
                      <Option>Oil Change</Option>
                      <Option>Air Filter</Option>
                    </Dropdown>
                  </div>

                  <div>
                    <Label htmlFor="cost">Cost</Label>
                    <Input
                      id="cost"
                      value={editCost}
                      onChange={(e, data) => setEditCost(data.value)}
                      style={{ width: '100%', marginTop: '8px' }}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    size="large"
                    rows={4}
                    value={editNotes}
                    onChange={(_, data) => setEditNotes(data.value)}
                    textarea={{ className: styles.notesTextarea }}
                  />
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setEditDialogOpen(false)}>
                CANCEL
              </Button>
              <Button appearance="primary" onClick={() => {
                console.log('Saving completed service:', {
                  tracker: editTracker,
                  completionDate: editCompletionDate,
                  odoAtCompletion: editOdoAtCompletion,
                  serviceType: editServiceType,
                  cost: editCost,
                  notes: editNotes,
                });
                setEditDialogOpen(false);
              }}>
                SAVE
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
